import * as Sentry from '@sentry/react'

/**
 * Sentry init — error monitoring для frontend.
 *
 * Проект `contentboard-frontend` в org `maksmedia-ht`.
 * Раньше JS-ошибки на фронте уходили в никуда: ChunkErrorBoundary их ловил
 * и показывал overlay «Обновление приложения», скрывая корень проблемы.
 * Это привело к тому что баги типа PR #75 (VideoDetailModal crash из-за
 * {status:...} объекта в transcription) находились только когда юзер
 * лично приходил со скриншотом.
 *
 * Теперь любая необработанная JS-ошибка автоматически репортится сюда:
 * https://maksmedia-ht.sentry.io/projects/contentboard-frontend/
 *
 * DSN — публичный идентификатор проекта, не секрет (см. docs.sentry.io —
 * DSN identifies the project but cannot read data).
 */
const DSN = import.meta.env.VITE_SENTRY_DSN
  || 'https://716306de985bce135eac7bed1df0e7c0@o4508884922204160.ingest.de.sentry.io/4511376712925264'

export function initSentry() {
  // В dev режиме обычно не нужен — слишком шумно, плюс не нужно
  // ловить ошибки в HMR-стейте. Включаем только в production-build.
  // Можно форсировать через VITE_SENTRY_FORCE=1 для тестов.
  const enabled = import.meta.env.PROD || import.meta.env.VITE_SENTRY_FORCE === '1'

  if (!enabled) {
    return
  }

  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.VITE_SENTRY_ENV || 'production',
    // Release tag для группировки по версии bundle. Vite не имеет
    // built-in commit-SHA, но можно прокинуть через build args.
    // Sentry авто-сгруппирует по нему source maps когда настроим upload.
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,

    // 10% sampling для performance (transactions). Errors всегда 100%.
    // Низкий performance sampling потому что у нас нет приоритета
    // на perf monitoring — главное errors.
    tracesSampleRate: 0.1,

    // Игнорируем шумные «expected» ошибки которые не баги:
    ignoreErrors: [
      // Chunk loading failures — у нас есть отдельный ChunkErrorBoundary
      // который автоперезагружает страницу. В Sentry это будет шум.
      /Failed to fetch dynamically imported module/i,
      /Loading chunk/i,
      /Loading CSS chunk/i,
      // Mobile Safari iOS вариант той же chunk-ошибки (CONTENTBOARD-FRONTEND-1/-3)
      /Importing a module script failed/i,
      /error loading dynamically imported module/i,
      // Browser extensions inject errors иногда
      /ResizeObserver loop limit exceeded/,
      /Non-Error promise rejection captured/,
      // Telegram Mini App SDK, инжектируется in-app WebView когда страницу
      // открывают внутри Telegram. У нас НЕТ @telegram-apps зависимости — это не
      // наш код; bridge падает на методах, которых нет в хосте
      // (CONTENTBOARD-FRONTEND-4). Безвредно, 0 затронутых юзеров.
      /Error invoking postEvent/i,
      /postEvent.*Method not found/i,
    ],

    // Не репортить ошибки из расширений Chrome/Firefox
    denyUrls: [
      /chrome-extension:/i,
      /moz-extension:/i,
      /safari-extension:/i,
    ],

    // Default integrations включают:
    // - browserApiErrorsIntegration (window.onerror + unhandledrejection)
    // - breadcrumbsIntegration (console, navigation, fetch, xhr, ui)
    // - httpClientIntegration (fetch/xhr breadcrumbs)
    // — этого хватает для базового error capture
  })
}

// Re-export для использования в ChunkErrorBoundary
export { Sentry }
